import { flatten } from "@s-libs/micro-dash";

/**
 * @param {Any} paramValue The value to check.
 * @returns A boolean indicating if the input value can be readily embedded
 *  into a search params string.
 */
const isValidSearchParamValue = (paramValue: unknown): boolean => {
  return (
    typeof paramValue === "string" ||
    Array.isArray(paramValue) ||
    paramValue === null ||
    typeof paramValue === "number" ||
    typeof paramValue === "boolean"
  );
};

/**
 * @param {Any} paramValue The value to encode.
 * @returns The value, stringified to be valid for embedding into a search
 *  params string. Or undefined if the value cannot be readily embedded.
 */
const encodeSearchParamValue = (
  paramValue: string | number | boolean,
): string | undefined => {
  if (!isValidSearchParamValue(paramValue)) return undefined;
  if (!Array.isArray(paramValue)) return encodeURIComponent(paramValue);
  return (
    flatten(paramValue)
      .map(encodeSearchParamValue)
      .filter((encodedValue) => encodedValue !== undefined)
      // Joining with ',' *after* encoding ensures that commas separating array
      // items remain literal, not encoded.
      .join(",")
  );
};

/**
 * @param {Object} search An object to convert into a search params string.
 * @returns A search params string.
 */
const stringifyParams = (
  search:
    | { [key: string]: string | number | boolean | null | undefined }
    | undefined,
): `?${string}` | "" => {
  if (!search) {
    return "";
  }

  const entryStrings = Object.keys(search)
    .map((paramKey) => {
      const encodedKey = encodeURIComponent(paramKey);
      if (!encodedKey) return undefined;
      const paramValue = search[paramKey];
      const encodedValue = paramValue && encodeSearchParamValue(paramValue);
      if (encodedValue === undefined) return undefined;

      return `${encodedKey}=${encodedValue}`;
    })
    .filter((entry) => entry !== undefined)
    // Sorting ensures consistent ordering of search params, making string
    // equality comparison for search param strings less brittle.
    .sort();
  if (entryStrings.length === 0) return "";
  return `?${entryStrings.join("&")}`;
};

export default stringifyParams;
